import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { getPayroll, getCutOffHour } from 'api/payroll';
import searchConsultants, { ConsultantOption } from 'api/cases/searchConsultants';
import { useNotificationProvider } from 'hooks';
import { ADMIN_ROLES } from 'containers/pages/payrollReport/constants';
import { PayrollParams, DateGroupRow } from 'containers/pages/payrollReport/types';
import { groupPatrolsByShiftDate, groupCommentsByDate } from 'containers/pages/payrollReport/libs/groupByDate';
import { mapGuardTableData, mapConsultantTableData } from 'containers/pages/payrollReport/libs/mapTableData';
import {
  calculateGuardGrandTotal,
  calculateConsultantGrandTotal,
} from 'containers/pages/payrollReport/libs/calculateHours';

function usePayrollReport() {
  const { userInfo } = useSelector(selectUserInfo);
  const { notifyError } = useNotificationProvider();

  const isAdmin = useMemo(
    () => ADMIN_ROLES.some((role) => userInfo.roles.includes(role)),
    [userInfo.roles],
  );

  const [params, setParams] = useState<PayrollParams>(() => ({
    StartDate: null,
    EndDate: null,
    ConsultantId: isAdmin ? null : userInfo.employeeId,
    ConsultantName: isAdmin ? '' : userInfo.fullname,
    ReportType: isAdmin ? '' : 'guard',
  }));

  const [cutOffHour, setCutOffHour] = useState<number>(6);
  const [dateGroups, setDateGroups] = useState<DateGroupRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Consultant autocomplete state
  const [consultantOptions, setConsultantOptions] = useState<ConsultantOption[]>([]);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canFetch = Boolean(
    params.StartDate && params.EndDate && params.ConsultantId && params.ReportType,
  );

  const grandTotal = useMemo(() => {
    if (dateGroups.length === 0) return '';
    if (params.ReportType === 'guard') return calculateGuardGrandTotal(dateGroups);
    if (params.ReportType === 'consultant') return calculateConsultantGrandTotal(dateGroups);
    return '';
  }, [dateGroups, params.ReportType]);

  // Fetch cutoff hour on mount
  useEffect(() => {
    const fetchCutOff = async () => {
      try {
        const result = await getCutOffHour();
        if (result?.Payload?.Id) {
          setCutOffHour(result.Payload.Id);
        }
      } catch (error) {
        console.error('[PayrollReport] Failed to fetch cutoff hour:', error);
      }
    };
    fetchCutOff();
  }, []);

  // Auto-fetch when all params are filled
  useEffect(() => {
    if (!canFetch) return;

    const fetchPayroll = async () => {
      setIsLoading(true);
      setHasFetched(true);
      try {
        const fetchProps = {
          StartDate: params.StartDate,
          EndDate: params.EndDate,
          ConsultantId: params.ConsultantId,
          ReportType: params.ReportType,
        };
        const result = await getPayroll(fetchProps);
        const records = result?.Payload || result?.Data || [];

        if (params.ReportType === 'guard') {
          const grouped = groupPatrolsByShiftDate(records, cutOffHour);
          setDateGroups(mapGuardTableData(grouped));
        } else {
          const grouped = groupCommentsByDate(records);
          setDateGroups(mapConsultantTableData(grouped));
        }
      } catch (error) {
        console.error('[PayrollReport] Failed to fetch payroll:', error);
        notifyError('Failed to load payroll data');
        setDateGroups([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.StartDate, params.EndDate, params.ConsultantId, params.ReportType, cutOffHour]);

  const onParamsChange = useCallback((updates: Partial<PayrollParams>) => {
    setParams((prev) => ({ ...prev, ...updates }));
    setHasFetched(false);
  }, []);

  const onConsultantSearch = useCallback((keyword: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!keyword || keyword.length < 2) {
      setConsultantOptions([]);
      return;
    }
    setConsultantLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const results = await searchConsultants(keyword);
        setConsultantOptions(results);
      } catch (error) {
        console.error('[PayrollReport] Consultant search failed:', error);
        setConsultantOptions([]);
      } finally {
        setConsultantLoading(false);
      }
    }, 300);
  }, []);

  const onConsultantSelect = useCallback(
    (_event: unknown, value: ConsultantOption | null) => {
      onParamsChange({
        ConsultantId: value?.Id ?? null,
        ConsultantName: value?.FullName ?? '',
      });
    },
    [onParamsChange],
  );

  return {
    state: {
      params,
      dateGroups,
      isLoading,
      isAdmin,
      canFetch,
      grandTotal,
      hasFetched,
      consultantOptions,
      consultantLoading,
    },
    handler: {
      onParamsChange,
      onConsultantSearch,
      onConsultantSelect,
    },
  };
}

export default usePayrollReport;
